import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getFormatedDate } from '../../../../helper';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: 'relative',
    overflow: 'hidden',
},
sliderContainer: {
    margin: '20px 0',
    padding: '0 30px',
    position: 'relative',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
},
sliderWrapper: {
    position: 'relative',
    height: '8px',
    display: 'flex',
    alignItems: 'center',
},
slider: {
    WebkitAppearance: 'none',
    width: '100%',
    height: '4px',
    background: '#e0e0e0',
    borderRadius: '2px',
    outline: 'none',
    '&::-webkit-slider-thumb': {
        WebkitAppearance: 'none',
        width: '20px',
        height: '20px',
        background: '#0067A1',
        borderRadius: '50%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '&:hover': {
            transform: 'scale(1.1)',
            background: '#004d80',
        },
        '&:active': {
            transform: 'scale(1)',
        }
    },
},
pageIndicator: {
    position: 'absolute',
    right: '0',
    bottom: '-24px',
    color: '#666',
    fontSize: '0.8rem',
    fontWeight: 400,
    fontFamily: 'system-ui, sans-serif',
    '& span': {
        color: '#0067A1',
        fontWeight: 500,
    }
},
progressTrack: {
    position: 'absolute',
    height: '4px',
    background: '#0067A1',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
},
}));

const AccuracyChart = ({ activeTab, graphData = [], type }) => {
    const classes = useStyles();
    const [borderRadius, setBorderRadius] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [maxValue, setMaxValue] = useState(0);
    const [minBias, setMinBias] = useState(-1000);
    const [maxBias, setMaxBias] = useState(1000);
    const itemsPerPage = 10;

    useEffect(() => {
        const handleResize = () => {
            const screenWidth = window.innerWidth;
            if (screenWidth < 1000) {
                setBorderRadius(2);
            } else if (screenWidth < 1500) {
                setBorderRadius(5);
            } else {
                setBorderRadius(7);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(()=>{
        calculateMaximunValue();
        calculateMinMaxValueForBias();
    }, [graphData, type, activeTab]);

    const calculateMaximunValue = () => {
        const forecastData = graphData?.map((item) => item?.Forecast || 0);
        const actualData = graphData?.map((item) => item?.Actual || 0);
        const maxForecast = Math.max(...forecastData);
        const maxActual = Math.max(...actualData);
        if(maxForecast > maxActual){
            setMaxValue(maxForecast);
        }else{
            setMaxValue(maxActual);
        }

    };

    const calculateMinMaxValueForBias = () => {
        const biasData = graphData?.map((item) => item?.Bias || 0);
        setMinBias(Math.min(...biasData));
        setMaxBias(Math.max(...biasData));
    };

    const formatToKOrM = (value) => {
        if (value >= 1e6) {
            return `${(value / 1e6)?.toFixed(0)}M`;
        } else if (value >= 1e3) {
            return `${(value / 1e3)?.toFixed(0)}K`;
        }
        return value?.toFixed(0);
    };

    const totalItems = graphData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Determine the categories, series, and yAxisTitle dynamically based on activeTab
    let categories = [];
    let seriesData = [];
    let yAxisTitle = '';
 
    categories = graphData?.map((item) => getFormatedDate(item?.Date) || 'Unknown Metric');
    seriesData = [
        {
            name: "Forecast",
            type: "bar",
            data: graphData?.map((item) => item?.Forecast || 0),
            //data: [1240, 3200, 4600, 2000, 3500]
        },
        {
            name: 'Actual',
            type: "bar",
            data: graphData?.map((item) => item?.Actual || 0),
            //data: [140, 1200, 4000, 5000, 3000]
        },
        {
            name: activeTab === 'Bias' ? 'Bias' : 'Accuracy',
            type: "line",
            data: graphData?.map((item) => item[activeTab === 'Bias' ? 'Bias' : 'Accuracy'] || 0),
            //data: [90, 80, 50, 20, 40],
            
        },
    ];
    yAxisTitle = activeTab === "Diagnostics" ? "Sales" : "Throughput Pallets";


        // Slice data for current page
    const slicedCategories = categories?.slice(startIndex, endIndex);
    const slicedSeries = seriesData?.map(s => ({
        ...s,
        data: s.data.slice(startIndex, endIndex)
    }));


    const options = {
        chart: {
            height: 350,
            type: 'bar',
            stacked: false,
            toolbar: {
                show: false,
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '50%',
                borderRadius: borderRadius,
                borderRadiusApplication: 'end',
                borderRadiusWhenStacked: 'last',
            },
        },
        grid: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            width: 3,
            curve: 'straight',
        },
        series: seriesData,
        colors: ['#EC6FBD', '#A4D65D', '#0067A1', '#F7D240'],
        xaxis: {
            categories: slicedCategories,
        },
        yaxis: [
            {
                seriesName: ["Forecast", "Actual"],
                title: {
                    text: yAxisTitle,
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 500,
                        color: '#333',
                    },
                },
                labels: {
                    formatter: (value) => type === "Dollars" ? `$${formatToKOrM(value)}` : `${formatToKOrM(value)}`,
                },
                min: 0, // Set minimum value for right y-axis
                max: maxValue, // Set maximum value for right y-axis
              
            },
            // {
            //     show: false,
            // },
            {
                // Right y-axis for accuracy (percentage)
                opposite: true, // Place the axis on the right side
                seriesName: ["Accuracy", "Bias"],
                title: {
                    text: activeTab === 'Bias' ? 'Bias' : 'Accuracy',
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 500,
                        color: '#333',
                    },
                },
                labels: {
                    formatter: (value) => `${value.toFixed(0)}%`, // Format percentage on the right y-axis
                },
                min: activeTab === 'Bias' ? minBias : 0, // Set minimum value for right y-axis
                max: activeTab === 'Bias' ? maxBias : 100, // Set maximum value for right y-axis
            },
        ],
        tooltip: {
            shared: true,
            intersect: false,
            y: [
                {
                    formatter: (value) => type === "Dollars" ? `$${formatToKOrM(value)}` : `${formatToKOrM(value)}`,
                },
                {
                    formatter: (value) => type === "Dollars" ? `$${formatToKOrM(value)}` : `${formatToKOrM(value)}`,
                },
                {
                    formatter: (value) => `${value?.toFixed(0)}%`,
                }
            ]
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            markers: {
                width: 12,
                height: 12,
                radius: 10,
                shape: "circle",
            },
        },
    };

    return (
        <div className="grouped-bar-line-chart">
            <ReactApexChart options={options} series={slicedSeries} type="bar" height={350} />
            {totalPages > 1 && (
                <div className={classes.sliderContainer}>
                    <div className={classes.sliderWrapper}>
                        <div
                            className={classes.progressTrack}
                            style={{
                                width: `${((currentPage + 1) / totalPages) * 100}%`,
                                opacity: 0.3
                            }}
                        />
                        <input
                            type="range"
                            min="0"
                            max={totalPages - 1}
                            value={currentPage}
                            onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
                            className={classes.slider}
                        />
                    </div>
                    <div className={classes.pageIndicator}>
                        <span>{currentPage + 1}</span> / {totalPages}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccuracyChart;
